/**
 * Subscription detail: row fields, entitlements, statements, pay-api, actions.
 *
 * TMMPDM-0078 (tmm-api 0124): the Promotion field shows the applied promotion by kind, and End promotion
 * renders only when `appliedPromotion` is set (one server field). Its confirm says what happens by the
 * code's kind; a 409 `PROMOTION_NOT_APPLIED` is said inside the confirm, which stays open with nothing
 * left to confirm, and the row is re-read so the button is gone once the confirm closes.
 *
 * TMMPDM-0087: Move to the current version when the row's offer resolves to a customer card (the same
 * version-id map OffersPanel uses). A missing link shows no action. The server's move result says
 * whether the subscriber was already current; this dialog does not decide that.
 */
import { useEffect, useRef, useState, type ReactElement } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { ConfirmDialog, ErrorState, useNotify } from '@/components/page';
import { adminApi } from '@/api/tmm/admin';
import { Amount } from '@/components/ui';
import type {
  AdminSubscriptionDetail,
  AdminSubscriptionRow,
  AppliedPromotion,
} from '@/api/tmm/types';
import { CancelSubscriptionDialog } from './CancelSubscriptionDialog';
import { ChangeOfferDialog } from './ChangeOfferDialog';
import { CompDialog } from './CompDialog';
import { PromotionDialog } from './PromotionDialog';
import { AiSuspensionDialog } from './AiSuspensionDialog';
import { cardsByVersionId, currentVersion } from '../customerCards/customerRateCardModel';
import { ApiError, errorCopy } from '@/api/errors';
import { formatDate, formatMoney, formatPercentBp } from '@/format';
import { StatusChip } from '@/status';

interface SubscriptionDetailDialogProps {
  open: boolean;
  slug: string;
  subscriptionId: number | null;
  locale: string;
  resellerId: number | null;
  resellerName?: string | null | undefined;
  /** The tenant's currency, for TENANT_CURRENCY_NOT_PRICED (TMMPDM-0025). */
  currency?: string | undefined;
  onClose: () => void;
  onChanged: () => void;
}

function field(label: string, value: string | number | ReactElement | null | undefined) {
  return (
    <Box component="div" sx={{ display: 'grid', gridTemplateColumns: '12rem 1fr', gap: 1 }}>
      <Typography component="dt" variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography component="dd" variant="body2" sx={{ m: 0 }}>
        {value === undefined || value === null || value === '' ? '—' : value}
      </Typography>
    </Box>
  );
}

type Translate = ReturnType<typeof useCopy>['t'];

/** The terms of the promotion by kind, in the locale, never converted. `OFFER` has no discount to state. */
function promotionTerms(promotion: AppliedPromotion, locale: string, t: Translate): string | null {
  if (promotion.kind === 'PERCENT_OFF_VARIABLE' && promotion.percentBp !== null) {
    return t('subscriptions.promotion.termsPercent', '{{pct}} off usage', {
      pct: formatPercentBp(promotion.percentBp, locale),
    });
  }
  if (
    promotion.kind === 'AMOUNT_OFF_VARIABLE' &&
    promotion.amountMinor !== null &&
    promotion.currency !== null
  ) {
    return t('subscriptions.promotion.termsAmount', '{{amount}} off usage per statement', {
      amount: formatMoney(promotion.amountMinor, promotion.currency, locale),
    });
  }
  if (promotion.kind === 'OFFER') {
    return t('subscriptions.promotion.termsOffer', 'Promotional offer price');
  }
  return null;
}

/** What is left, for the variable kinds only: `OFFER` has no statement count. */
function promotionRemaining(promotion: AppliedPromotion, t: Translate): string | null {
  if (promotion.kind !== 'PERCENT_OFF_VARIABLE' && promotion.kind !== 'AMOUNT_OFF_VARIABLE') {
    return null;
  }
  if (promotion.statementsRemaining === null) {
    return t('subscriptions.promotion.untilEnded', 'Until ended');
  }
  return t('subscriptions.promotion.statementsLeft', '{{count}} statements left', {
    count: promotion.statementsRemaining,
  });
}

function appliedPromotionLines(
  promotion: AppliedPromotion,
  locale: string,
  t: Translate,
): ReactElement {
  const lines = [
    t('subscriptions.promotion.codeName', '{{code}} · {{name}}', {
      code: promotion.code,
      name: promotion.name,
    }),
    promotionTerms(promotion, locale, t),
    promotionRemaining(promotion, t),
    t('subscriptions.promotion.appliedOn', 'Applied {{date}}', {
      date: formatDate(promotion.appliedAt, locale),
    }),
  ].filter((line): line is string => line !== null);
  return (
    <>
      {lines.map((line) => (
        <Box key={line} component="span" sx={{ display: 'block' }}>
          {line}
        </Box>
      ))}
    </>
  );
}

export function SubscriptionDetailDialog({
  open,
  slug,
  subscriptionId,
  locale,
  resellerId,
  resellerName,
  currency,
  onClose,
  onChanged,
}: SubscriptionDetailDialogProps) {
  const { t } = useCopy();
  const notify = useNotify();
  const [detail, setDetail] = useState<AdminSubscriptionDetail | null>(null);
  // A failed read is the ErrorState (with Try again).
  const [loadFailure, setLoadFailure] = useState<{ cause: unknown } | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [changeOpen, setChangeOpen] = useState(false);
  const [compOpen, setCompOpen] = useState(false);
  const [promoOpen, setPromoOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiSuspended, setAiSuspended] = useState(false);
  const [endPromo, setEndPromo] = useState(false);
  // The promotion and subscriber the confirm was opened for: its words stay while the row reloads behind it.
  const [endTarget, setEndTarget] = useState<{
    promotion: AppliedPromotion;
    subscriber: string;
  } | null>(null);
  const [endingPromo, setEndingPromo] = useState(false);
  // A failed End promotion is said inside its own confirm, which stays open (TMM-B4).
  const [endPromoError, setEndPromoError] = useState<string | null>(null);
  // 409 PROMOTION_NOT_APPLIED: nothing is left to end, so the confirm can only be closed.
  const [endPromoGone, setEndPromoGone] = useState(false);
  // The card for the offer id this read answered. A missing link, a failed read, or a
  // different offer leaves Move hidden. The match is derived so the effect never clears state itself.
  const [moveResolved, setMoveResolved] = useState<{
    offerId: number;
    card: { id: number; name: string; version: number } | null;
  } | null>(null);
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveBusy, setMoveBusy] = useState(false);
  const [moveError, setMoveError] = useState<string | null>(null);
  const [moveNotice, setMoveNotice] = useState<{
    severity: 'info' | 'warning';
    text: string;
  } | null>(null);
  // alreadyCurrent: nothing left to do, so the confirm closes on Close.
  const [moveSettled, setMoveSettled] = useState(false);
  // Snapshotted at click so a later re-read cannot change the open confirm.
  const [moveTarget, setMoveTarget] = useState<{
    cardId: number;
    card: string;
    version: number;
    subscriber: string;
  } | null>(null);
  const applyButton = useRef<HTMLButtonElement>(null);
  // The End promotion button leaves once the promotion has ended; focus then goes to Apply promotion.
  const focusApplyWhenGone = useRef(false);

  const load = () => {
    if (subscriptionId == null) return;
    adminApi
      .getSubscription(slug, subscriptionId)
      .then((body) => {
        setDetail(body);
        setLoadFailure(null);
      })
      .catch((err: unknown) => {
        setDetail(null);
        setLoadFailure({ cause: err });
      });
  };

  useEffect(() => {
    if (!open || subscriptionId == null) return;
    let cancelled = false;
    adminApi
      .getSubscription(slug, subscriptionId)
      .then((body) => {
        if (!cancelled) {
          setDetail(body);
          setLoadFailure(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setDetail(null);
          setLoadFailure({ cause: err });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, slug, subscriptionId]);

  const offerId = detail?.subscription.retailOfferId;
  const moveCard =
    open && typeof offerId === 'number' && moveResolved?.offerId === offerId
      ? moveResolved.card
      : null;
  useEffect(() => {
    if (!open || typeof offerId !== 'number') return;
    let cancelled = false;
    const requested = offerId;
    void Promise.all([adminApi.listOffers(slug), adminApi.listCustomerRateCards()])
      .then(([offers, cards]) => {
        if (cancelled) return;
        const versionId = offers.offers.find((item) => item.id === requested)?.resellerRateCardId;
        const card =
          versionId === undefined ? undefined : cardsByVersionId(cards.rateCards).get(versionId);
        const current = card === undefined ? undefined : currentVersion(card);
        setMoveResolved({
          offerId: requested,
          card:
            card === undefined || current === undefined
              ? null
              : { id: card.id, name: card.name, version: current.version },
        });
      })
      .catch(() => {
        if (!cancelled) setMoveResolved({ offerId: requested, card: null });
      });
    return () => {
      cancelled = true;
    };
  }, [open, slug, offerId]);

  const afterAction = () => {
    load();
    onChanged();
  };

  const row: AdminSubscriptionRow | undefined = detail?.subscription;
  const promotion = row?.appliedPromotion ?? null;
  const subscriber = row?.displayName || row?.email || String(subscriptionId);

  const confirmMove = () => {
    if (subscriptionId == null || moveTarget === null) return;
    const target = moveTarget;
    setMoveBusy(true);
    setMoveError(null);
    setMoveNotice(null);
    void adminApi
      .moveCustomerRateCardSubscription(target.cardId, subscriptionId)
      .then((result) => {
        if (result.moved >= 1) {
          notify.success(
            t('subscriptions.move.moved', 'Moved to v{{version, number}}. A notice was sent.', {
              version: target.version,
            }),
          );
          setMoveOpen(false);
          setMoveSettled(false);
          afterAction();
          return;
        }
        if (result.alreadyCurrent >= 1) {
          setMoveSettled(true);
          setMoveNotice({
            severity: 'info',
            text: t(
              'subscriptions.move.already',
              'Already on the current version. Nothing changed.',
            ),
          });
          return;
        }
        setMoveNotice({
          severity: 'warning',
          text: t(
            'subscriptions.move.failed',
            "The move didn't complete. Try again; it's safe to repeat.",
          ),
        });
      })
      .catch((err: unknown) => {
        setMoveError(
          errorCopy(err, t, t('subscriptions.move.error', "Couldn't move this subscription.")),
        );
      })
      .finally(() => setMoveBusy(false));
  };

  // The End promotion button is gone with the promotion: once its confirm is closed, keep the focus
  // inside the dialog, on the action that comes next.
  const promotionGone = row !== undefined && promotion === null;
  useEffect(() => {
    if (endPromo || !promotionGone || !focusApplyWhenGone.current) return;
    focusApplyWhenGone.current = false;
    applyButton.current?.focus();
  }, [endPromo, promotionGone]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        {t('subscriptions.detail.title', 'Subscription')}
        {row ? (
          <Stack direction="row" spacing={1} sx={{ mt: 1, alignItems: 'center' }}>
            <Typography variant="body2">{row.email}</Typography>
            <StatusChip
              status={row.status}
              label={t(`subscription.status.${row.status}`, row.status)}
            />
            <Typography variant="body2">
              {t(`subscriptions.origin.${row.origin}`, row.origin)}
            </Typography>
          </Stack>
        ) : null}
      </DialogTitle>
      <DialogContent>
        {loadFailure ? (
          <ErrorState
            error={loadFailure.cause}
            message={t('subscriptions.detail.loadError', "Couldn't load this subscription.")}
            onRetry={load}
          />
        ) : null}
        {row && detail ? (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box component="dl" sx={{ m: 0 }}>
              {field(t('subscriptions.field.id', 'Id'), row.id)}
              {field(t('subscriptions.field.userId', 'User id'), row.userId)}
              {field(t('subscriptions.field.email', 'Email'), row.email)}
              {field(t('subscriptions.field.displayName', 'Name'), row.displayName)}
              {field(
                t('subscriptions.field.status', 'Status'),
                <StatusChip
                  status={row.status}
                  label={t(`subscription.status.${row.status}`, row.status)}
                />,
              )}
              {field(t('subscriptions.field.origin', 'Origin'), row.origin)}
              {field(t('subscriptions.field.retailOfferId', 'Offer'), row.retailOfferId)}
              {field(
                t('subscriptions.field.promotion', 'Promotion'),
                promotion === null ? undefined : appliedPromotionLines(promotion, locale, t),
              )}
              {field(
                t('subscriptions.field.trialEndsOn', 'Trial ends'),
                formatDate(row.trialEndsOn, locale),
              )}
              {field(
                t('subscriptions.field.compedUntil', 'Comped until'),
                formatDate(row.compedUntil, locale),
              )}
              {field(
                t('subscriptions.field.currentPeriodStart', 'Period start'),
                formatDate(row.currentPeriodStart, locale),
              )}
              {field(
                t('subscriptions.field.currentPeriodEnd', 'Period end'),
                formatDate(row.currentPeriodEnd, locale),
              )}
              {field(
                t('subscriptions.field.nextBillingOn', 'Next billing'),
                formatDate(row.nextBillingOn, locale),
              )}
              {field(t('subscriptions.field.cardBrand', 'Card brand'), row.cardBrand)}
              {field(t('subscriptions.field.maskedCard', 'Masked card'), row.maskedCard)}
              {field(
                t('subscriptions.field.payApiSubscriptionId', 'pay-api id'),
                row.payApiSubscriptionId,
              )}
              {field(
                t('subscriptions.field.reconciledAt', 'Reconciled'),
                formatDate(row.reconciledAt, locale),
              )}
              {field(t('subscriptions.field.createdBy', 'Created by'), row.createdBy)}
              {field(
                t('subscriptions.field.createdAt', 'Created'),
                formatDate(row.createdAt, locale),
              )}
              {field(
                t('subscriptions.field.updatedAt', 'Updated'),
                formatDate(row.updatedAt, locale),
              )}
            </Box>

            <Typography variant="subtitle2">
              {t('subscriptions.detail.entitlements', 'Entitlements')}
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table
                size="small"
                aria-label={t('subscriptions.detail.entitlements', 'Entitlements')}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>{t('subscriptions.entitlement.kind', 'Kind')}</TableCell>
                    <TableCell>{t('subscriptions.entitlement.status', 'Status')}</TableCell>
                    <TableCell>{t('subscriptions.entitlement.startsAt', 'Starts')}</TableCell>
                    <TableCell>{t('subscriptions.entitlement.endsAt', 'Ends')}</TableCell>
                    <TableCell>{t('subscriptions.entitlement.sponsor', 'Sponsor')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detail.entitlements.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5}>—</TableCell>
                    </TableRow>
                  ) : (
                    detail.entitlements.map((item, index) => (
                      <TableRow key={`${item.kind}-${item.startsAt ?? index}`}>
                        <TableCell>{item.kind}</TableCell>
                        <TableCell>
                          <StatusChip
                            status={item.status}
                            label={t(`seats.status.${item.status}`, item.status)}
                          />
                        </TableCell>
                        <TableCell>{formatDate(item.startsAt, locale)}</TableCell>
                        <TableCell>{formatDate(item.endsAt, locale)}</TableCell>
                        <TableCell>{item.sponsorDisplayName ?? '—'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="subtitle2">
              {t('subscriptions.detail.statements', 'Statements')}
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small" aria-label={t('statements.tableAria', 'Tenant statements')}>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('statements.col.period', 'Period')}</TableCell>
                    <TableCell>{t('statements.col.status', 'Status')}</TableCell>
                    <TableCell>{t('statements.col.renewal', 'Renewal')}</TableCell>
                    <TableCell>{t('statements.col.seats', 'Seats')}</TableCell>
                    <TableCell>{t('statements.col.tax', 'Tax')}</TableCell>
                    <TableCell>{t('statements.col.total', 'Total')}</TableCell>
                    <TableCell>{t('statements.col.attempts', 'Attempts')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detail.statements.map((stmt) => (
                    <TableRow key={stmt.id}>
                      <TableCell>
                        {formatDate(stmt.periodStart, locale)} –{' '}
                        {formatDate(stmt.periodEnd, locale)}
                      </TableCell>
                      <TableCell>
                        <StatusChip
                          status={stmt.chargeStatus}
                          label={t(
                            `statements.chargeStatus.${stmt.chargeStatus}`,
                            stmt.chargeStatus,
                          )}
                        />
                      </TableCell>
                      <TableCell>
                        <Amount
                          minor={stmt.renewalMinor}
                          currency={stmt.currency}
                          locale={locale}
                        />
                      </TableCell>
                      <TableCell>
                        {stmt.seats} ×{' '}
                        <Amount minor={stmt.seatsMinor} currency={stmt.currency} locale={locale} />
                      </TableCell>
                      <TableCell>
                        <Amount minor={stmt.taxMinor} currency={stmt.currency} locale={locale} />
                      </TableCell>
                      <TableCell>
                        <strong>
                          <Amount
                            minor={stmt.totalMinor}
                            currency={stmt.currency}
                            locale={locale}
                          />
                        </strong>
                      </TableCell>
                      <TableCell>{stmt.attempts}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="subtitle2">
              {t('subscriptions.detail.payApi', 'pay-api')}
            </Typography>
            {detail.payApi ? (
              <Box component="dl" sx={{ m: 0 }}>
                {field(
                  t('subscriptions.payApi.subscriptionId', 'Id'),
                  detail.payApi.subscriptionId,
                )}
                {field(t('subscriptions.payApi.status', 'Status'), detail.payApi.status)}
                {field(
                  t('subscriptions.payApi.planMappingId', 'Plan mapping'),
                  detail.payApi.planMappingId,
                )}
                {field(t('subscriptions.payApi.contactRef', 'Contact'), detail.payApi.contactRef)}
                {field(
                  t('subscriptions.payApi.verticalRef', 'Vertical'),
                  detail.payApi.verticalRef,
                )}
                {field(
                  t('subscriptions.payApi.createdAt', 'Created'),
                  formatDate(detail.payApi.createdAt, locale),
                )}
                {field(
                  t('subscriptions.payApi.nextBillingOn', 'Next billing'),
                  formatDate(detail.payApi.nextBillingOn, locale),
                )}
                {field(
                  t('subscriptions.payApi.pauseEffectiveOn', 'Pause effective'),
                  formatDate(detail.payApi.pauseEffectiveOn, locale),
                )}
                {field(t('subscriptions.payApi.cardBrand', 'Card brand'), detail.payApi.cardBrand)}
                {field(
                  t('subscriptions.payApi.maskedCard', 'Masked card'),
                  detail.payApi.maskedCard,
                )}
                {field(
                  t('subscriptions.payApi.pspSubscriptionId', 'PSP id'),
                  detail.payApi.pspSubscriptionId,
                )}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t('subscriptions.detail.noPayApi', 'No pay-api record')}
              </Typography>
            )}

            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
              <Button onClick={() => setCancelOpen(true)}>
                {t('subscriptions.actions.cancel', 'Cancel…')}
              </Button>
              <Button onClick={() => setChangeOpen(true)}>
                {t('subscriptions.actions.changeOffer', 'Change offer…')}
              </Button>
              {row.origin !== 'COMP' ? (
                <Button onClick={() => setCompOpen(true)}>
                  {t('subscriptions.actions.comp', 'Comp…')}
                </Button>
              ) : null}
              <Button ref={applyButton} onClick={() => setPromoOpen(true)}>
                {t('subscriptions.actions.applyPromotion', 'Apply promotion…')}
              </Button>
              {promotion !== null ? (
                <Button
                  onClick={() => {
                    setEndPromoError(null);
                    setEndPromoGone(false);
                    setEndTarget({ promotion, subscriber });
                    focusApplyWhenGone.current = true;
                    setEndPromo(true);
                  }}
                >
                  {t('subscriptions.actions.endPromotion', 'End promotion')}
                </Button>
              ) : null}
              <Button onClick={() => setAiOpen(true)}>
                {t('subscriptions.actions.aiSuspension', 'AI suspension')}
              </Button>
              {moveCard ? (
                <Button
                  onClick={() => {
                    setMoveError(null);
                    setMoveNotice(null);
                    setMoveSettled(false);
                    setMoveTarget({
                      cardId: moveCard.id,
                      card: moveCard.name,
                      version: moveCard.version,
                      subscriber,
                    });
                    setMoveOpen(true);
                  }}
                >
                  {t('subscriptions.actions.moveToCurrent', 'Move to current version…')}
                </Button>
              ) : null}
            </Stack>
          </Stack>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('subscriptions.detail.close', 'Close')}</Button>
      </DialogActions>

      {subscriptionId != null ? (
        <>
          <CancelSubscriptionDialog
            open={cancelOpen}
            slug={slug}
            subscriptionId={subscriptionId}
            subscriber={subscriber}
            locale={locale}
            onClose={() => setCancelOpen(false)}
            onDone={afterAction}
          />
          <ChangeOfferDialog
            open={changeOpen}
            slug={slug}
            subscriptionId={subscriptionId}
            resellerId={resellerId}
            resellerName={resellerName}
            currency={currency}
            onClose={() => setChangeOpen(false)}
            onDone={() => {
              setChangeOpen(false);
              afterAction();
            }}
          />
          <CompDialog
            open={compOpen}
            slug={slug}
            subscriptionId={subscriptionId}
            resellerId={resellerId}
            resellerName={resellerName}
            currency={currency}
            onClose={() => setCompOpen(false)}
            onDone={() => {
              setCompOpen(false);
              afterAction();
            }}
          />
          <PromotionDialog
            open={promoOpen}
            slug={slug}
            subscriptionId={subscriptionId}
            onClose={() => setPromoOpen(false)}
            onDone={() => {
              setPromoOpen(false);
              afterAction();
            }}
            onChanged={afterAction}
          />
          <AiSuspensionDialog
            open={aiOpen}
            slug={slug}
            subscriptionId={subscriptionId}
            suspended={aiSuspended}
            onSuspendedChange={setAiSuspended}
            onClose={() => setAiOpen(false)}
            onApplied={() => {
              afterAction();
            }}
          />
          <ConfirmDialog
            open={endPromo}
            title={t('subscriptions.promotion.endTitle', 'End promotion')}
            consequence={
              endTarget?.promotion.kind === 'OFFER'
                ? t(
                    'subscriptions.promotion.endBodyOffer',
                    "End {{code}} for {{subscriber}}? Their price doesn't change. This lets you apply a different code.",
                    { code: endTarget.promotion.code, subscriber: endTarget.subscriber },
                  )
                : t(
                    'subscriptions.promotion.endBodyVariable',
                    "End {{code}} for {{subscriber}}? The discount stops from the next statement. Statements already issued keep it. You can apply the code again later while it's still valid.",
                    {
                      code: endTarget?.promotion.code ?? '',
                      subscriber: endTarget?.subscriber ?? '',
                    },
                  )
            }
            confirmLabel={t('subscriptions.promotion.endConfirm', 'End promotion')}
            cancelLabel={
              endPromoGone
                ? t('common.close', 'Close')
                : t('subscriptions.promotion.endKeep', 'Keep promotion')
            }
            onCancel={() => {
              // A kept promotion keeps its button, which gets the focus back from the dialog.
              if (!endPromoGone) focusApplyWhenGone.current = false;
              setEndPromo(false);
            }}
            destructive={endTarget?.promotion.kind !== 'OFFER'}
            confirmDisabled={endPromoGone}
            busy={endingPromo}
            error={endPromoError}
            onConfirm={() => {
              setEndingPromo(true);
              setEndPromoError(null);
              void adminApi
                .endPromotion(slug, subscriptionId)
                .then(() => {
                  setEndPromo(false);
                  afterAction();
                })
                .catch((err: unknown) => {
                  if (err instanceof ApiError && String(err.code) === 'PROMOTION_NOT_APPLIED') {
                    // Nothing is applied now: the row is re-read, so the button is gone once this closes.
                    setEndPromoGone(true);
                    load();
                  }
                  setEndPromoError(
                    errorCopy(
                      err,
                      t,
                      t('subscriptions.promotion.endError', "Couldn't end the promotion."),
                    ),
                  );
                })
                .finally(() => setEndingPromo(false));
            }}
          />
          <ConfirmDialog
            open={moveOpen}
            title={t('subscriptions.move.title', 'Move to the current version?')}
            consequence={
              <>
                {t(
                  'subscriptions.move.consequence',
                  "{{subscriber}} moves to {{card}} v{{version, number}} (the current version). They're sent a notice. If they're already on it, nothing changes.",
                  {
                    subscriber: moveTarget?.subscriber ?? '',
                    card: moveTarget?.card ?? '',
                    version: moveTarget?.version ?? 0,
                  },
                )}
                {moveNotice ? (
                  <Alert severity={moveNotice.severity} sx={{ mt: 2 }}>
                    {moveNotice.text}
                  </Alert>
                ) : null}
              </>
            }
            confirmLabel={t('subscriptions.move.confirm', 'Move')}
            cancelLabel={moveSettled ? t('common.close', 'Close') : t('common.cancel', 'Cancel')}
            confirmDisabled={moveSettled}
            busy={moveBusy}
            error={moveError}
            onCancel={() => {
              setMoveOpen(false);
              setMoveError(null);
              setMoveNotice(null);
              setMoveSettled(false);
            }}
            onConfirm={confirmMove}
          />
        </>
      ) : null}
    </Dialog>
  );
}
